// ============================================================================
// STAKEHOLDER DOCUMENTS TAB
// Full CRUD for contact documents inside WorkItemDetailModal.
// Inline add/edit form with file upload (50 MB limit), all fields from
// the legacy DocumentModal.
// Date: March 2026
// ============================================================================

'use client';

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  FileText,
  Search,
  Plus,
  Upload,
  Download,
  ExternalLink,
  Eye,
  Calendar,
  AlertTriangle,
  Shield,
  MapPin,
  User,
  Clock,
  Save,
  Loader2,
  Trash2,
  ChevronLeft,
  Edit,
  X,
  Image as ImageIcon,
  FileSpreadsheet,
  FileVideo,
  FileAudio,
} from 'lucide-react';
import type { WorkItem } from '../UniversalWorkItems';
import { useContactConfig } from '@/hooks/useContactConfig';
import { authService } from '@/lib/authService';
import { notify, confirm } from '@/lib/notifications';
import { uploadToCompanyDrive } from '@/lib/companyDriveUpload';

const getAuthToken = () => authService.getTokens()?.access_token || null;

// ============================================================================
// Types
// ============================================================================

interface StakeholderDocumentsTabProps {
  workItem: WorkItem;
  isDark: boolean;
  onRefresh?: () => void;
}

interface StakeholderDocument {
  id: number;
  contact_id: number;
  location_id?: number | null;
  contact_person_id?: number | null;
  document_type?: string | null;
  document_name: string;
  id_number?: string | null;
  description?: string | null;
  file_url?: string | null;
  file_size?: number | null;
  mime_type?: string | null;
  issue_date?: string | null;
  expiry_date?: string | null;
  is_expired?: boolean;
  is_verified?: boolean;
  location_name?: string | null;
  person_name?: string | null;
  contact_company_name?: string | null;
}

interface ContactPersonOption {
  id: number;
  first_name: string;
  last_name: string;
  id_number?: string | null;
  is_director?: boolean;
}

interface DocumentFormData {
  contact_id: number;
  location_id: number | null;
  contact_person_id: number | null;
  document_type: string;
  document_name: string;
  id_number: string;
  description: string;
  file_url: string;
  issue_date: string;
  expiry_date: string;
}

// ============================================================================
// Helpers
// ============================================================================

const DOCUMENT_TYPE_LABELS: Record<string, { label: string; color: { light: string; dark: string } }> = {
  sla: { label: 'SLA', color: { light: 'bg-blue-100 text-blue-700', dark: 'bg-blue-900/30 text-blue-300' } },
  contract: { label: 'Contract', color: { light: 'bg-green-100 text-green-700', dark: 'bg-green-900/30 text-green-300' } },
  mou: { label: 'MOU', color: { light: 'bg-purple-100 text-purple-700', dark: 'bg-purple-900/30 text-purple-300' } },
  nda: { label: 'NDA', color: { light: 'bg-indigo-100 text-indigo-700', dark: 'bg-indigo-900/30 text-indigo-300' } },
  restraint: { label: 'Restraint', color: { light: 'bg-red-100 text-red-700', dark: 'bg-red-900/30 text-red-300' } },
  certificate: { label: 'Certificate', color: { light: 'bg-amber-100 text-amber-700', dark: 'bg-amber-900/30 text-amber-300' } },
  insurance: { label: 'Insurance', color: { light: 'bg-cyan-100 text-cyan-700', dark: 'bg-cyan-900/30 text-cyan-300' } },
  license: { label: 'License', color: { light: 'bg-teal-100 text-teal-700', dark: 'bg-teal-900/30 text-teal-300' } },
  permit: { label: 'Permit', color: { light: 'bg-lime-100 text-lime-700', dark: 'bg-lime-900/30 text-lime-300' } },
  agreement: { label: 'Agreement', color: { light: 'bg-emerald-100 text-emerald-700', dark: 'bg-emerald-900/30 text-emerald-300' } },
  specification: { label: 'Specification', color: { light: 'bg-violet-100 text-violet-700', dark: 'bg-violet-900/30 text-violet-300' } },
  bee_certificate: { label: 'BEE Cert', color: { light: 'bg-emerald-100 text-emerald-700', dark: 'bg-emerald-900/30 text-emerald-300' } },
  tax_clearance: { label: 'Tax Clearance', color: { light: 'bg-orange-100 text-orange-700', dark: 'bg-orange-900/30 text-orange-300' } },
  id_document: { label: 'ID Document', color: { light: 'bg-slate-100 text-slate-700', dark: 'bg-slate-700/70 text-slate-200' } },
  other: { label: 'Document', color: { light: 'bg-gray-100 text-gray-700', dark: 'bg-gray-700 text-gray-300' } },
};

const DOCUMENT_TYPE_OPTIONS = [
  'contract', 'certificate', 'license', 'insurance', 'permit', 'agreement',
  'specification', 'sla', 'mou', 'nda', 'restraint', 'bee_certificate', 'tax_clearance', 'id_document', 'other',
];

function formatDate(v?: string | null) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatFileSize(b?: number | null) {
  if (!b) return null;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function isExpiringSoon(dt?: string | null): boolean {
  if (!dt) return false;
  const exp = new Date(dt);
  if (isNaN(exp.getTime())) return false;
  const days = (exp.getTime() - Date.now()) / 86400000;
  return days > 0 && days <= 30;
}

function getFileExtension(value?: string | null) {
  if (!value) return '';
  const clean = value.split('?')[0].split('#')[0];
  const filename = clean.split('/').pop() || '';
  const dotIndex = filename.lastIndexOf('.');
  return dotIndex >= 0 ? filename.slice(dotIndex + 1).toLowerCase() : '';
}

function inferDocumentMimeType(fileUrl?: string | null, mimeType?: string | null) {
  if (mimeType) return mimeType.toLowerCase();
  const extension = getFileExtension(fileUrl);

  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(extension)) return 'image/*';
  if (extension === 'pdf') return 'application/pdf';
  if (['mp4', 'webm', 'mov', 'm4v'].includes(extension)) return 'video/*';
  if (['mp3', 'wav', 'ogg', 'aac', 'm4a', 'flac'].includes(extension)) return 'audio/*';
  if (['txt', 'md', 'csv', 'json', 'xml', 'log', 'sql', 'yaml', 'yml'].includes(extension)) return 'text/plain';
  if (['html', 'htm'].includes(extension)) return 'text/html';
  if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension)) return 'application/vnd.ms-office';

  return 'application/octet-stream';
}

function getDocumentPreviewType(fileUrl?: string | null, mimeType?: string | null) {
  const resolvedMimeType = inferDocumentMimeType(fileUrl, mimeType);
  const extension = getFileExtension(fileUrl);

  if (resolvedMimeType.startsWith('image/')) return 'image' as const;
  if (resolvedMimeType === 'application/pdf') return 'pdf' as const;
  if (resolvedMimeType.startsWith('video/')) return 'video' as const;
  if (resolvedMimeType.startsWith('audio/')) return 'audio' as const;
  if (resolvedMimeType.startsWith('text/') || ['txt', 'md', 'csv', 'json', 'xml', 'log', 'sql', 'yaml', 'yml', 'html', 'htm'].includes(extension)) return 'browser' as const;
  if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension)) return 'office' as const;
  return 'unsupported' as const;
}

function getOfficePreviewUrl(fileUrl: string) {
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
}

const EMPTY_DOC_FORM: DocumentFormData = {
  contact_id: 0, location_id: null, contact_person_id: null,
  document_type: 'contract', document_name: '', id_number: '', description: '',
  file_url: '', issue_date: '', expiry_date: '',
};

// ============================================================================
// Component
// ============================================================================

export default function StakeholderDocumentsTab({ workItem, isDark, onRefresh }: StakeholderDocumentsTabProps) {
  const { config } = useContactConfig();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [editingDocument, setEditingDocument] = useState<StakeholderDocument | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<StakeholderDocument | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Related data for form selects
  const [contactLocations, setContactLocations] = useState<Array<{ id: number; location_name: string }>>([]);
  const [contactPeople, setContactPeople] = useState<ContactPersonOption[]>([]);
  const [formData, setFormData] = useState<DocumentFormData>({ ...EMPTY_DOC_FORM });

  const cf = useMemo(() => {
    if (!workItem.custom_fields) return {} as Record<string, any>;
    return typeof workItem.custom_fields === 'string' ? JSON.parse(workItem.custom_fields) : workItem.custom_fields;
  }, [workItem.custom_fields]);

  const documents = useMemo<StakeholderDocument[]>(() => Array.isArray(cf.related_documents) ? cf.related_documents : [], [cf.related_documents]);
  const relatedPeople = useMemo<ContactPersonOption[]>(() => Array.isArray(cf.related_people) ? cf.related_people : [], [cf.related_people]);
  const contactId = cf.entity_id || workItem.contact_id;
  const showForm = isAdding || !!editingDocument;
  const selectedPerson = useMemo(
    () => contactPeople.find((person) => person.id === formData.contact_person_id) || relatedPeople.find((person) => person.id === formData.contact_person_id),
    [contactPeople, relatedPeople, formData.contact_person_id]
  );

  // Load locations + people when form opens
  useEffect(() => {
    if (!showForm || !contactId) return;
    (async () => {
      try {
        const { fetchContactPeople } = await import('@/lib/contacts/fetchContactPeople');
        const [locRes, contactPeople] = await Promise.all([
          fetch(`/api/v1/contacts/${contactId}/locations/`, { headers: { Authorization: `Bearer ${getAuthToken()}` } }),
          fetchContactPeople<ContactPersonOption>({ contactId, token: getAuthToken() }),
        ]);
        if (locRes.ok) { const { data } = await locRes.json(); setContactLocations(data || []); }
        setContactPeople(contactPeople || []);
      } catch { /* ignore */ }
    })();
  }, [showForm, contactId]);

  // Stats
  const stats = useMemo(() => {
    const expired = documents.filter(d => d.is_expired).length;
    const expiring = documents.filter(d => !d.is_expired && isExpiringSoon(d.expiry_date)).length;
    const verified = documents.filter(d => d.is_verified).length;
    return { total: documents.length, expired, expiring, verified };
  }, [documents]);

  // Unique doc types for filter
  const documentTypes = useMemo(() => {
    const s = new Set(documents.map(d => d.document_type || 'other'));
    return Array.from(s).sort();
  }, [documents]);

  const filteredDocuments = useMemo(() => {
    let result = documents;
    if (filterType !== 'all') result = result.filter(d => (d.document_type || 'other') === filterType);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(d => d.document_name.toLowerCase().includes(q) || (d.id_number || '').toLowerCase().includes(q) || (d.document_type || '').toLowerCase().includes(q) || (d.description || '').toLowerCase().includes(q) || (d.location_name || '').toLowerCase().includes(q) || (d.person_name || '').toLowerCase().includes(q));
    }
    return result;
  }, [documents, filterType, searchQuery]);

  const resetForm = useCallback((doc?: StakeholderDocument | null) => {
    if (doc) {
      setFormData({
        contact_id: doc.contact_id || contactId,
        location_id: doc.location_id || null,
        contact_person_id: doc.contact_person_id || null,
        document_type: doc.document_type || 'contract',
        document_name: doc.document_name || '',
        id_number: doc.id_number || '',
        description: doc.description || '',
        file_url: doc.file_url || '',
        issue_date: doc.issue_date ? doc.issue_date.slice(0, 10) : '',
        expiry_date: doc.expiry_date ? doc.expiry_date.slice(0, 10) : '',
      });
    } else {
      setFormData({ ...EMPTY_DOC_FORM, contact_id: contactId });
    }
  }, [contactId]);

  const handleAdd = () => { setEditingDocument(null); resetForm(); setIsAdding(true); };
  const handleEdit = (doc: StakeholderDocument) => { setIsAdding(false); setEditingDocument(doc); resetForm(doc); };
  const handleCancel = () => { setIsAdding(false); setEditingDocument(null); };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { notify.error('File size must be less than 50 MB'); return; }
    setUploading(true);
    try {
      const result = await uploadToCompanyDrive(file, `stakeholder-documents/contact-${formData.contact_id || contactId || 'general'}`);
      if (result.success && result.directUrl) {
        const url = result.directUrl || result.url || '';
        const autoName = !formData.document_name ? file.name.replace(/\.[^/.]+$/, '') : formData.document_name;
        setFormData(p => ({ ...p, file_url: url, document_name: autoName }));
        notify.success('File uploaded');
      } else {
        notify.error(result.error || 'File upload failed');
      }
    } catch {
      notify.error('File upload failed');
    } finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!formData.document_name) { notify.error('Document name is required'); return; }
    setLoading(true);
    try {
      const url = editingDocument?.id ? `/api/v1/contacts/documents/${editingDocument.id}` : '/api/v1/contacts/documents/';
      const res = await fetch(url, {
        method: editingDocument?.id ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, contact_id: formData.contact_id || contactId }),
      });
      if (res.ok) { notify.success(editingDocument?.id ? 'Document updated' : 'Document created'); handleCancel(); onRefresh?.(); }
      else { const d = await res.json(); notify.error(d.error || 'Failed to save document'); }
    } catch { notify.error('Failed to save document'); } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!editingDocument?.id) return;
    await confirm({
      title: 'Remove Document', message: `Remove "${editingDocument.document_name}" from this stakeholder? This is a soft delete and can be restored in the database if needed.`,
      confirmText: 'Remove', variant: 'danger',
      onConfirm: async () => {
        try {
          const res = await fetch(`/api/v1/contacts/documents/${editingDocument.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getAuthToken()}` } });
          if (res.ok) { notify.success('Document removed (soft delete)'); handleCancel(); onRefresh?.(); }
          else { const d = await res.json(); notify.error(d.error || 'Failed to remove document'); }
        } catch { notify.error('Failed to remove document'); }
      },
    });
  };

  const cardBg = isDark ? 'bg-gray-800/60 border-gray-700' : 'bg-white border-gray-200';
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm ${isDark ? 'border-gray-600 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'}`;
  const labelClass = `block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`;

  const renderDocumentPreview = useCallback((doc: Pick<StakeholderDocument, 'document_name' | 'file_url' | 'mime_type'>, heightClass = 'h-[65vh]', disablePointerEvents = false) => {
    if (!doc.file_url) {
      return (
        <div className={`flex items-center justify-center rounded-xl border border-dashed p-8 text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
          No file uploaded for this document yet.
        </div>
      );
    }

    const previewType = getDocumentPreviewType(doc.file_url, doc.mime_type);
    const previewInteractionClass = disablePointerEvents ? ' pointer-events-none' : '';

    if (previewType === 'image') {
      return (
        <div className={`overflow-hidden rounded-xl border ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-gray-50'}`}>
          {/* eslint-disable-next-line @next/next/no-img-element -- remote uploaded document preview */}
          <img src={doc.file_url} alt={doc.document_name} className={`w-full ${heightClass} object-contain${previewInteractionClass}`} />
        </div>
      );
    }

    if (previewType === 'pdf' || previewType === 'browser') {
      return (
        <iframe
          src={doc.file_url}
          className={`w-full ${heightClass} rounded-xl border${previewInteractionClass} ${isDark ? 'border-gray-700 bg-white' : 'border-gray-200 bg-white'}`}
          title={`${doc.document_name} preview`}
        />
      );
    }

    if (previewType === 'office') {
      return (
        <iframe
          src={getOfficePreviewUrl(doc.file_url)}
          className={`w-full ${heightClass} rounded-xl border${previewInteractionClass} ${isDark ? 'border-gray-700 bg-white' : 'border-gray-200 bg-white'}`}
          title={`${doc.document_name} preview`}
        />
      );
    }

    if (previewType === 'video') {
      return (
        <div className={`overflow-hidden rounded-xl border ${isDark ? 'border-gray-700 bg-black' : 'border-gray-200 bg-black'}`}>
          <video src={doc.file_url} controls className={`w-full ${heightClass}${previewInteractionClass}`} />
        </div>
      );
    }

    if (previewType === 'audio') {
      return (
        <div className={`rounded-xl border p-8 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex flex-col items-center gap-4">
            <div className={`flex h-16 w-16 items-center justify-center rounded-full ${isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-600'}`}>
              <FileAudio className="h-8 w-8" />
            </div>
            <audio src={doc.file_url} controls className={`w-full max-w-lg${previewInteractionClass}`} />
          </div>
        </div>
      );
    }

    const extension = getFileExtension(doc.file_url);
    const fallbackIcon = ['xls', 'xlsx'].includes(extension)
      ? FileSpreadsheet
      : ['mp4', 'webm', 'mov', 'm4v'].includes(extension)
        ? FileVideo
        : ['mp3', 'wav', 'ogg', 'aac', 'm4a', 'flac'].includes(extension)
          ? FileAudio
          : ImageIcon;
    const FallbackIcon = fallbackIcon;

    return (
      <div className={`rounded-xl border p-8 text-center ${isDark ? 'border-gray-700 bg-gray-900/60 text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
        <FallbackIcon className="mx-auto mb-3 h-10 w-10" />
        <p className="text-sm font-medium">Preview is not available for this file type in the browser.</p>
        <p className="mt-1 text-xs opacity-80">You can still open or download the document.</p>
      </div>
    );
  }, [isDark]);

  const renderDocumentThumbnail = useCallback((doc: Pick<StakeholderDocument, 'document_name' | 'file_url' | 'mime_type'>) => {
    if (!doc.file_url) {
      return (
        <div className={`flex h-20 w-20 items-center justify-center rounded-xl border ${isDark ? 'border-gray-700 bg-gray-900/60 text-gray-500' : 'border-gray-200 bg-gray-50 text-gray-400'}`}>
          <FileText className="h-7 w-7" />
        </div>
      );
    }

    const previewType = getDocumentPreviewType(doc.file_url, doc.mime_type);
    const extension = getFileExtension(doc.file_url).toUpperCase() || 'FILE';

    if (previewType === 'image') {
      return (
        <div className={`h-20 w-20 overflow-hidden rounded-xl border ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-gray-50'}`}>
          {/* eslint-disable-next-line @next/next/no-img-element -- small uploaded document thumbnail */}
          <img src={doc.file_url} alt={doc.document_name} className="h-full w-full object-cover" />
        </div>
      );
    }

    if (previewType === 'pdf' || previewType === 'browser' || previewType === 'office') {
      return (
        <div className={`relative h-20 w-20 overflow-hidden rounded-xl border ${isDark ? 'border-gray-700 bg-white' : 'border-gray-200 bg-white'}`}>
          <iframe
            src={previewType === 'office' ? getOfficePreviewUrl(doc.file_url) : doc.file_url}
            className="pointer-events-none h-full w-full scale-[2.2] origin-top-left"
            title={`${doc.document_name} thumbnail`}
          />
          <div className={`absolute inset-x-0 bottom-0 px-1.5 py-1 text-center text-[10px] font-semibold ${isDark ? 'bg-gray-900/80 text-gray-100' : 'bg-white/90 text-gray-700'}`}>
            {extension}
          </div>
        </div>
      );
    }

    if (previewType === 'video') {
      return (
        <div className={`relative flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border ${isDark ? 'border-gray-700 bg-black text-white' : 'border-gray-200 bg-gray-900 text-white'}`}>
          <FileVideo className="h-8 w-8" />
          <div className="absolute inset-x-0 bottom-0 bg-black/60 px-1.5 py-1 text-center text-[10px] font-semibold">
            VIDEO
          </div>
        </div>
      );
    }

    if (previewType === 'audio') {
      return (
        <div className={`relative flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border ${isDark ? 'border-gray-700 bg-blue-950/40 text-blue-300' : 'border-gray-200 bg-blue-50 text-blue-600'}`}>
          <FileAudio className="h-8 w-8" />
          <div className={`absolute inset-x-0 bottom-0 px-1.5 py-1 text-center text-[10px] font-semibold ${isDark ? 'bg-blue-950/80 text-blue-100' : 'bg-blue-100/90 text-blue-700'}`}>
            AUDIO
          </div>
        </div>
      );
    }

    return (
      <div className={`relative flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border ${isDark ? 'border-gray-700 bg-gray-900/60 text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
        <FileText className="h-8 w-8" />
        <div className={`absolute inset-x-0 bottom-0 px-1.5 py-1 text-center text-[10px] font-semibold ${isDark ? 'bg-gray-900/80 text-gray-100' : 'bg-white/90 text-gray-700'}`}>
          {extension}
        </div>
      </div>
    );
  }, [isDark]);

  // ---------------------------------------------------------------------------
  // FORM VIEW
  // ---------------------------------------------------------------------------
  if (showForm) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button onClick={handleCancel} className={`flex items-center gap-1 text-sm font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}>
            <ChevronLeft className="w-4 h-4" /> Back to list
          </button>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{editingDocument?.id ? 'Edit Document' : 'New Document'}</h3>
        </div>

        <div className={`rounded-xl border p-5 space-y-5 ${cardBg}`}>
          {/* Location + Person selects */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Location (Optional)</label>
              <select value={formData.location_id || ''} onChange={e => setFormData(p => ({ ...p, location_id: e.target.value ? parseInt(e.target.value) : null }))} className={inputClass}>
                <option value="">All locations</option>
                {contactLocations.map(l => <option key={l.id} value={l.id}>{l.location_name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Person (Optional)</label>
              <select value={formData.contact_person_id || ''} onChange={e => setFormData(p => ({ ...p, contact_person_id: e.target.value ? parseInt(e.target.value) : null }))} className={inputClass}>
                <option value="">All people</option>
                {contactPeople.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}{p.is_director ? ' (Director)' : ''}</option>)}
              </select>
              {formData.document_type === 'id_document' && selectedPerson?.id_number && (
                <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {selectedPerson.first_name} {selectedPerson.last_name}{selectedPerson.is_director ? ' (Director)' : ''} · ID {selectedPerson.id_number}
                </p>
              )}
            </div>
          </div>

          {/* Name + Type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className={labelClass}>Document Name *</label><input type="text" value={formData.document_name} onChange={e => setFormData(p => ({ ...p, document_name: e.target.value }))} className={inputClass} placeholder="e.g. Service Agreement 2025" /></div>
            {formData.document_type === 'id_document' && (
              <div className="sm:col-span-2">
                <label className={labelClass}>ID Number</label>
                <input type="text" value={formData.id_number} onChange={e => setFormData(p => ({ ...p, id_number: e.target.value }))} className={inputClass} placeholder="Enter the ID/passport number on this document" />
              </div>
            )}
            <div>
              <label className={labelClass}>Document Type</label>
              <select value={formData.document_type} onChange={e => setFormData(p => ({
                ...p,
                document_type: e.target.value,
                document_name: p.document_name || (e.target.value === 'id_document' && selectedPerson
                  ? `${selectedPerson.first_name} ${selectedPerson.last_name} ID Document`
                  : p.document_name)
              }))} className={inputClass}>
                {DOCUMENT_TYPE_OPTIONS.map(t => {
                  const info = DOCUMENT_TYPE_LABELS[t] || DOCUMENT_TYPE_LABELS.other;
                  return <option key={t} value={t}>{info.label}</option>;
                })}
              </select>
            </div>
            <div>
              <label className={labelClass}>Upload File</label>
              <input ref={fileInputRef} type="file" onChange={handleFileUpload} disabled={uploading} className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg" />
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className={`flex items-center justify-center gap-2 w-full px-3 py-2 border rounded-lg cursor-pointer text-sm ${uploading ? 'cursor-not-allowed' : ''} ${isDark ? 'border-gray-600 bg-gray-800 hover:bg-gray-700 text-gray-300' : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-700'}`}>
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4" /> Choose file</>}
              </button>
              {formData.file_url && (
                <div className="mt-2 flex items-center gap-2">
                  <a href={formData.file_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline truncate max-w-[200px]">{formData.file_url.split('/').pop()}</a>
                  <button type="button" onClick={() => setFormData(p => ({ ...p, file_url: '' }))} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                </div>
              )}
            </div>
          </div>

          {formData.file_url && (
            <div>
              <label className={labelClass}>Document Preview</label>
              {renderDocumentPreview({ document_name: formData.document_name || 'Uploaded document', file_url: formData.file_url, mime_type: null }, 'h-[50vh]')}
            </div>
          )}

          {/* Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelClass}>Issue Date</label><input type="date" value={formData.issue_date} onChange={e => setFormData(p => ({ ...p, issue_date: e.target.value }))} className={inputClass} /></div>
            <div><label className={labelClass}>Expiry Date</label><input type="date" value={formData.expiry_date} onChange={e => setFormData(p => ({ ...p, expiry_date: e.target.value }))} className={inputClass} /></div>
          </div>

          {/* Description */}
          <div><label className={labelClass}>Description</label><textarea value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} rows={3} className={inputClass} /></div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
            {editingDocument?.id ? (
              <button onClick={handleDelete} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"><Trash2 className="w-4 h-4" /> Remove</button>
            ) : <div />}
            <div className="flex gap-3">
              <button onClick={handleCancel} className={`px-4 py-2 text-sm font-medium rounded-lg ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}>Cancel</button>
              <button onClick={handleSave} disabled={loading || uploading} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingDocument?.id ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // LIST VIEW
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-4" data-tour-id="stakeholder-documents-tab">
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total', value: stats.total, color: isDark ? 'text-gray-100' : 'text-gray-900' },
          { label: 'Verified', value: stats.verified, color: 'text-green-600' },
          { label: 'Expiring Soon', value: stats.expiring, color: 'text-amber-600' },
          { label: 'Expired', value: stats.expired, color: 'text-red-600' },
        ].map(s => (
          <div key={s.label} className={`rounded-lg border p-3 ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{s.label}</p>
            <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
          <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{config.documents_label || 'Documents'}</h3>
          <span className={`text-sm px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{documents.length}</span>
        </div>
        <button onClick={handleAdd} data-tour-id="stakeholder-documents-add" className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"><Plus className="w-4 h-4" /> Add Document</button>
      </div>

      {/* Search + Filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by name, type, description..." data-tour-id="stakeholder-documents-search" className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'}`} />
        </div>
        {documentTypes.length > 1 && (
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className={`px-3 py-2 border rounded-lg text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
            <option value="all">All Types</option>
            {documentTypes.map(t => { const info = DOCUMENT_TYPE_LABELS[t] || DOCUMENT_TYPE_LABELS.other; return <option key={t} value={t}>{info.label}</option>; })}
          </select>
        )}
      </div>

      {filteredDocuments.length === 0 ? (
        <div className={`text-center py-12 rounded-xl border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <FileText className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{searchQuery || filterType !== 'all' ? 'No documents match your filters' : 'No documents added yet'}</p>
          {!searchQuery && filterType === 'all' && <button onClick={handleAdd} className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-700">Add the first document</button>}
        </div>
      ) : (
        <div className="space-y-2" data-tour-id="stakeholder-documents-list">
          {filteredDocuments.map(doc => {
            const typeInfo = DOCUMENT_TYPE_LABELS[doc.document_type || ''] || DOCUMENT_TYPE_LABELS.other;
            const expiring = !doc.is_expired && isExpiringSoon(doc.expiry_date);
            const previewType = getDocumentPreviewType(doc.file_url, doc.mime_type);
            const canRenderInline = Boolean(doc.file_url) && previewType !== 'unsupported';
            return (
              <div key={doc.id} onClick={() => handleEdit(doc)} className={`rounded-xl border p-4 cursor-pointer hover:ring-2 hover:ring-blue-500/40 transition-shadow ${cardBg}`}>
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center gap-2 flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${doc.is_expired ? (isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-600') : expiring ? (isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-600') : (isDark ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-600')}`}>
                      <FileText className="w-5 h-5" />
                    </div>
                    {renderDocumentThumbnail(doc)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-medium text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{doc.document_name}</span>
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${isDark ? typeInfo.color.dark : typeInfo.color.light}`}>{typeInfo.label}</span>
                          {doc.is_expired && <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'}`}><AlertTriangle className="w-3 h-3" /> Expired</span>}
                          {expiring && <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700'}`}><Clock className="w-3 h-3" /> Expiring Soon</span>}
                          {doc.is_verified && <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'}`}><Shield className="w-3 h-3" /> Verified</span>}
                        </div>
                        {doc.description && <p className={`text-xs mt-1 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{doc.description}</p>}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
                          {doc.id_number && <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}><User className="w-3.5 h-3.5" /> ID {doc.id_number}</span>}
                          {doc.issue_date && <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}><Calendar className="w-3.5 h-3.5" /> Issued {formatDate(doc.issue_date)}</span>}
                          {doc.expiry_date && <span className={`inline-flex items-center gap-1 text-xs ${doc.is_expired ? (isDark ? 'text-red-400' : 'text-red-600') : expiring ? (isDark ? 'text-amber-400' : 'text-amber-600') : (isDark ? 'text-gray-400' : 'text-gray-500')}`}><Calendar className="w-3.5 h-3.5" /> Expires {formatDate(doc.expiry_date)}</span>}
                          {doc.location_name && <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}><MapPin className="w-3.5 h-3.5" /> {doc.location_name}</span>}
                          {doc.person_name && <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}><User className="w-3.5 h-3.5" /> {doc.person_name}</span>}
                          {doc.file_size && <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{formatFileSize(doc.file_size)}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {doc.file_url && (
                          <>
                            <button type="button" onClick={e => { e.stopPropagation(); setPreviewDocument(doc); }} className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} title="Preview"><Eye className="w-4 h-4" /></button>
                            <a href={doc.file_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} title="Open"><ExternalLink className="w-4 h-4" /></a>
                            <a href={doc.file_url} download onClick={e => e.stopPropagation()} className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} title="Download"><Download className="w-4 h-4" /></a>
                          </>
                        )}
                        <Edit className={`w-4 h-4 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                      </div>
                    </div>

                    {doc.file_url && (
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <p className={`text-[11px] font-medium uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Inline preview
                          </p>
                          {canRenderInline && (
                            <button
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                setPreviewDocument(doc);
                              }}
                              className={`text-xs font-medium ${isDark ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-700'}`}
                            >
                              Open larger preview
                            </button>
                          )}
                        </div>
                        {canRenderInline ? (
                          <div
                            className="rounded-xl"
                            onClick={e => {
                              e.stopPropagation();
                              setPreviewDocument(doc);
                            }}
                          >
                            {renderDocumentPreview(doc, 'h-[28vh]', true)}
                          </div>
                        ) : (
                          <div className={`rounded-xl border border-dashed px-4 py-3 text-xs ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                            This file type cannot be rendered inline here yet, but the thumbnail and open/download actions are available.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {previewDocument && (
        <div className="fixed inset-0 z-[190] flex items-center justify-center bg-black/75 p-4">
          <div className={`relative w-full max-w-6xl rounded-2xl border shadow-2xl ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
            <div className={`flex items-center justify-between border-b px-5 py-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="min-w-0">
                <h4 className={`truncate text-base font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{previewDocument.document_name}</h4>
                <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {DOCUMENT_TYPE_LABELS[previewDocument.document_type || 'other']?.label || 'Document'}
                  {previewDocument.id_number ? ` · ID ${previewDocument.id_number}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 pl-4">
                {previewDocument.file_url && (
                  <>
                    <a href={previewDocument.file_url} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm ${isDark ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                      <ExternalLink className="h-4 w-4" /> Open
                    </a>
                    <a href={previewDocument.file_url} download className={`inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm ${isDark ? 'bg-blue-900/40 text-blue-200 hover:bg-blue-900/60' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}>
                      <Download className="h-4 w-4" /> Download
                    </a>
                  </>
                )}
                <button type="button" onClick={() => setPreviewDocument(null)} className={`rounded-lg p-2 ${isDark ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`} aria-label="Close preview">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-5">
              {renderDocumentPreview(previewDocument)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}